import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {AsyncPipe, NgOptimizedImage} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {DeviceService} from '../../ngm-dev-blocks/utils/services';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AuthQuery} from '../state/auth.query';
import {AuthService} from '../services/auth.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

import {ForgotPasswordComponent} from '../forgot-password/forgot-password.component';
import {take} from 'rxjs';

@Component({
  selector: 'app-auth',
  imports: [
    AsyncPipe,
    NgOptimizedImage,
    ReactiveFormsModule,
    MatProgressSpinner,

  ],
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthComponent {
  private deviceService = inject(DeviceService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private authQuery = inject(AuthQuery);
  private dialogRef = inject(MatDialogRef<AuthComponent>);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);


  // Device observables
  isHandset$ = this.deviceService.isHandset$;
  isTablet$ = this.deviceService.isTablet$;
  isLessThanMD$ = this.deviceService.isLessThanMD$;

  // Toggle between sign up and sign in
  isSignUp = signal(true);
  // Auth state signals
  loading = this.authQuery.loading;
  error = this.authQuery.error;
  validationErrors = this.authQuery.validationErrors;
  // Forms
  signUpForm: FormGroup = this.fb.group({
    firstName: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
    lastName: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
    email: ['', [Validators.required, Validators.email]],
    phoneNumber: ['', [Validators.required, Validators.pattern(/^\+?[1-9]\d{1,14}$/)]],
    password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(72)]],
    acceptTerms: [false, [Validators.requiredTrue]]
  });

  signInForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]]
  });




  toggleAuthMode() {
    this.isSignUp.update(value => !value);
  }

  onSubmit() {
    if (this.isSignUp()) {
      if (this.signUpForm.valid) {
        const formValue = {
          ...this.signUpForm.value,
          confirmPassword: this.signUpForm.value.password
        };

        this.authService.register(formValue).subscribe({
          next: () => {
            this.showSuccessToast('Inscription réussie! Bienvenue!');
            this.dialogRef.close();
          },
          error: () => {
            this.showErrorToast();
          }
        });
      }
    } else {
      if (this.signInForm.valid) {
        this.authService.login(this.signInForm.value).subscribe({
          next: () => {
            this.showSuccessToast('Connexion réussie! Bienvenue!');
            this.dialogRef.close();
          },
          error: () => {
            this.showErrorToast();
          }
        });
      }
    }
  }

  private showSuccessToast(message: string) {
    this.snackBar.open(message, 'Fermer', {
      duration: 4000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      panelClass: ['toast-success']
    });
  }

  private showErrorToast() {
    const validationErrors = this.validationErrors();
    const generalError = this.error();

    if (validationErrors && validationErrors.length > 0) {
      // Show validation errors
      if (validationErrors.length === 1) {
        // Single validation error - show the message directly
        this.snackBar.open(validationErrors[0].message, 'Fermer', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['toast-error']
        });
      } else {
        // Multiple validation errors - show summary with action to see details
        const message = `${validationErrors.length} erreurs de validation`;
        const snackBarRef = this.snackBar.open(message, 'Voir détails', {
          duration: 6000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['toast-error']
        });

        snackBarRef.onAction().subscribe(() => {
          this.showValidationErrorsDialog(validationErrors);
        });
      }
    } else if (generalError) {
      // Show general error
      this.snackBar.open(generalError, 'Fermer', {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['toast-error']
      });
    } else {
      // Fallback error
      this.snackBar.open('Une erreur est survenue', 'Fermer', {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['toast-error']
      });
    }
  }

  private showValidationErrorsDialog(errors: any[]) {
    const errorList = errors.map(e => `• ${e.message}`).join('\n');
    this.snackBar.open(errorList, 'Fermer', {
      duration: 8000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['toast-error', 'toast-multiline']
    });
  }

  signInWithGoogle() {
    console.log('Sign in with Google');
  }


  openForgotPassword() {
    // Close all open dialogs
    this.dialog.closeAll();

    // Wait for all dialogs to be closed, then open new one
    const checkInterval = setInterval(() => {
      if (this.dialog.openDialogs.length === 0) {
        clearInterval(checkInterval);

        this.dialog.open(ForgotPasswordComponent, {
          width: 'auto',
          maxWidth: '1200px',
          minWidth: '320px',
          height: 'auto',
          maxHeight: '90vh',
          panelClass: 'auth-dialog',
          disableClose: false,
          hasBackdrop: true,
          backdropClass: 'auth-backdrop-blur'
        });
      }
    }, 50);
  }

}
